import { useState } from 'react';
import { AlertTriangle, Check, Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { useWizardStore, type FitGateDraft } from '../lib/wizard-store';

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-gray-400">{children}</p>;
}

const FIT_GATE_ERROR_CODES = {
  ai_not_configured: "L'IA n'est pas configurée pour cet environnement.",
  llm_upstream_failed: "L'assistant IA n'a pas répondu correctement. Réessaie dans quelques secondes.",
  llm_bad_format: "La suggestion IA a renvoyé un format inattendu.",
  ERR_VALIDATION: 'Le contexte envoyé à l’assistant IA est incomplet ou invalide.',
};

interface FitGateSuggestion {
  brand_scope: string;
  ideal_customer_profile: string;
  anti_profile: string;
  preferred_roles: string[];
  preferred_sectors: string[];
  preferred_company_types: string[];
  blocked_roles: string[];
  blocked_organization_types: string[];
  blocked_sectors: string[];
  blocked_countries: string[];
  blocked_languages: string[];
  bad_fit_examples: string[];
  workspace_suppressions: string[];
  brand_suppressions: string[];
  campaign_suppressions: string[];
  notes: string[];
}

function linesFromText(value: string): string[] {
  return value
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function mergeListField(existing: string, incoming: string[]): string {
  const merged = Array.from(new Set([...linesFromText(existing), ...incoming.map((entry) => entry.trim())]))
    .filter(Boolean);
  return merged.join('\n');
}

function preferExisting(existing: string, incoming: string): string {
  return existing.trim() || incoming.trim();
}

function buildAiPatch(current: FitGateDraft, suggestion: FitGateSuggestion): Partial<FitGateDraft> {
  return {
    brand_scope: preferExisting(current.brand_scope, suggestion.brand_scope),
    ideal_customer_profile: preferExisting(
      current.ideal_customer_profile,
      suggestion.ideal_customer_profile,
    ),
    anti_profile: preferExisting(current.anti_profile, suggestion.anti_profile),
    preferred_roles: mergeListField(current.preferred_roles, suggestion.preferred_roles),
    preferred_sectors: mergeListField(current.preferred_sectors, suggestion.preferred_sectors),
    preferred_company_types: mergeListField(
      current.preferred_company_types,
      suggestion.preferred_company_types,
    ),
    blocked_roles: mergeListField(current.blocked_roles, suggestion.blocked_roles),
    blocked_organization_types: mergeListField(
      current.blocked_organization_types,
      suggestion.blocked_organization_types,
    ),
    blocked_sectors: mergeListField(current.blocked_sectors, suggestion.blocked_sectors),
    blocked_countries: mergeListField(current.blocked_countries, suggestion.blocked_countries),
    blocked_languages: mergeListField(current.blocked_languages, suggestion.blocked_languages),
    bad_fit_examples: mergeListField(current.bad_fit_examples, suggestion.bad_fit_examples),
    workspace_suppressions: mergeListField(
      current.workspace_suppressions,
      suggestion.workspace_suppressions,
    ),
    brand_suppressions: mergeListField(current.brand_suppressions, suggestion.brand_suppressions),
    campaign_suppressions: mergeListField(
      current.campaign_suppressions,
      suggestion.campaign_suppressions,
    ),
  };
}

export function EligibilityAndSuppression() {
  const {
    fit_gate,
    patchFitGate,
    sender_company_name,
    sender_description,
    sender_website,
    offer_title,
    offer_description,
    offer_url,
    target_country,
    target_region,
    target_city,
    language,
  } = useWizardStore();
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [assistantNotes, setAssistantNotes] = useState<string[]>([]);
  const [assistantMessage, setAssistantMessage] = useState<string | null>(null);
  const [assistantError, setAssistantError] = useState<string | null>(null);

  const setField = (key: keyof typeof fit_gate, value: string | number) => {
    patchFitGate({ [key]: value } as Partial<typeof fit_gate>);
  };

  async function runAssistant() {
    setLoadingSuggestion(true);
    setAssistantError(null);
    setAssistantMessage(null);
    setAssistantNotes([]);

    try {
      const { data } = await apiClient.post<{
        suggestion: FitGateSuggestion;
        model?: string;
        websiteUsed?: string | null;
      }>('/v1/llm/suggest-fit-gate', {
        senderCompanyName: sender_company_name || null,
        senderDescription: sender_description || null,
        senderWebsite: sender_website || null,
        offerTitle: offer_title || null,
        offerDescription: offer_description || null,
        offerUrl: offer_url || null,
        targetCountry: target_country || null,
        targetRegion: target_region || null,
        targetCity: target_city || null,
        language,
        currentDraft: fit_gate,
      });

      patchFitGate(buildAiPatch(fit_gate, data.suggestion));
      setAssistantNotes(data.suggestion.notes ?? []);
      setAssistantMessage(
        data.websiteUsed
          ? 'Pré-rempli avec l’IA à partir de ton contexte et du site expéditeur.'
          : 'Pré-rempli avec l’IA à partir du contexte campagne.',
      );
    } catch (error) {
      setAssistantError(getApiErrorMessage(error, FIT_GATE_ERROR_CODES));
    } finally {
      setLoadingSuggestion(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Eligibility & suppression"
        subtitle="Définis qui peut entrer dans la campagne, qui passe en review, et qui doit être exclu avant tout upload provider."
      />
      <CardContent className="space-y-5" data-testid="eligibility-suppression-section">
        <div
          className="rounded-md border border-dashed border-brand-primary/40 bg-brand-primary/5 p-4 space-y-3"
          data-testid="fit-gate-ai-assistant"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm text-gray-700">
              <p className="font-medium text-brand-dark">Assistant IA de qualification</p>
              <p className="mt-1 text-xs text-gray-500">
                Pré-remplit l’ICP, les exclusions, les mauvais fits et les suppressions utiles
                sans écraser brutalement tes listes existantes.
              </p>
              {assistantError && (
                <span className="mt-2 flex items-start gap-1 text-xs text-red-600">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  {assistantError}
                </span>
              )}
              {assistantMessage && (
                <span className="mt-2 flex items-start gap-1 text-xs text-emerald-700">
                  <Check size={12} className="mt-0.5 shrink-0" />
                  {assistantMessage}
                </span>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={runAssistant}
              disabled={loadingSuggestion}
              data-testid="fit-gate-ai-button"
            >
              {loadingSuggestion ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Analyse…
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Pré-remplir avec l’IA
                </>
              )}
            </Button>
          </div>

          {assistantNotes.length > 0 && (
            <ul className="space-y-1 text-xs text-gray-600" data-testid="fit-gate-ai-notes">
              {assistantNotes.map((note) => (
                <li key={note}>• {note}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fit-brand-scope">Brand scope</Label>
            <Input
              id="fit-brand-scope"
              value={fit_gate.brand_scope}
              onChange={(event) => setField('brand_scope', event.target.value)}
              placeholder="acme-fr"
              data-testid="fit-brand-scope"
            />
            <Hint>
              Utilise ce scope pour partager une blacklist ou des exclusions entre campagnes d
              une même marque.
            </Hint>
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-ideal-profile">Ideal customer profile</Label>
            <Input
              id="fit-ideal-profile"
              value={fit_gate.ideal_customer_profile}
              onChange={(event) => setField('ideal_customer_profile', event.target.value)}
              placeholder="PME B2B avec equipe commerciale active"
              data-testid="fit-ideal-profile"
            />
            <Hint>Leads clairement alignés avec la campagne.</Hint>
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="fit-anti-profile">Anti-profile</Label>
          <Textarea
            id="fit-anti-profile"
            value={fit_gate.anti_profile}
            onChange={(event) => setField('anti_profile', event.target.value)}
            placeholder="Collectivites, particuliers, concurrents, profils sans lien avec l offre"
            rows={3}
            data-testid="fit-anti-profile"
          />
          <Hint>Ce contexte sert de garde-fou pour les cas franchement hors sujet.</Hint>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fit-preferred-roles">Preferred roles</Label>
            <Textarea
              id="fit-preferred-roles"
              value={fit_gate.preferred_roles}
              onChange={(event) => setField('preferred_roles', event.target.value)}
              placeholder="Head of sales, directeur commercial"
              rows={3}
              data-testid="fit-preferred-roles"
            />
            <Hint>Comma ou retour ligne. Ces rôles poussent vers send.</Hint>
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-preferred-sectors">Preferred sectors</Label>
            <Textarea
              id="fit-preferred-sectors"
              value={fit_gate.preferred_sectors}
              onChange={(event) => setField('preferred_sectors', event.target.value)}
              placeholder="Services B2B, SaaS"
              rows={3}
              data-testid="fit-preferred-sectors"
            />
            <Hint>Permet de renforcer le score sur les leads vraiment dans ta cible.</Hint>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fit-preferred-company-types">Preferred company types</Label>
            <Textarea
              id="fit-preferred-company-types"
              value={fit_gate.preferred_company_types}
              onChange={(event) => setField('preferred_company_types', event.target.value)}
              placeholder="PME, cabinet, agence"
              rows={2}
              data-testid="fit-preferred-company-types"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-bad-fit-examples">Bad-fit examples</Label>
            <Textarea
              id="fit-bad-fit-examples"
              value={fit_gate.bad_fit_examples}
              onChange={(event) => setField('bad_fit_examples', event.target.value)}
              placeholder="Maire d un village&#10;Association locale&#10;Etudiant"
              rows={3}
              data-testid="fit-bad-fit-examples"
            />
            <Hint>
              Ces exemples nourrissent la couche IA tout en restant lisibles pour les ops.
            </Hint>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fit-blocked-roles">Blocked roles</Label>
            <Textarea
              id="fit-blocked-roles"
              value={fit_gate.blocked_roles}
              onChange={(event) => setField('blocked_roles', event.target.value)}
              placeholder="Maire, stagiaire, etudiant"
              rows={3}
              data-testid="fit-blocked-roles"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-blocked-org-types">Blocked organization types</Label>
            <Textarea
              id="fit-blocked-org-types"
              value={fit_gate.blocked_organization_types}
              onChange={(event) => setField('blocked_organization_types', event.target.value)}
              placeholder="Collectivite, association, ecole"
              rows={3}
              data-testid="fit-blocked-org-types"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fit-blocked-sectors">Blocked sectors</Label>
            <Textarea
              id="fit-blocked-sectors"
              value={fit_gate.blocked_sectors}
              onChange={(event) => setField('blocked_sectors', event.target.value)}
              placeholder="Secteur public"
              rows={2}
              data-testid="fit-blocked-sectors"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-blocked-countries">Blocked countries / languages</Label>
            <Textarea
              id="fit-blocked-countries"
              value={fit_gate.blocked_countries}
              onChange={(event) => setField('blocked_countries', event.target.value)}
              placeholder="CH, DE"
              rows={2}
              data-testid="fit-blocked-countries"
            />
            <Textarea
              id="fit-blocked-languages"
              value={fit_gate.blocked_languages}
              onChange={(event) => setField('blocked_languages', event.target.value)}
              placeholder="de, it"
              rows={2}
              data-testid="fit-blocked-languages"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fit-blocked-domains">Blocked domains</Label>
            <Textarea
              id="fit-blocked-domains"
              value={fit_gate.blocked_domains}
              onChange={(event) => setField('blocked_domains', event.target.value)}
              placeholder="example.com"
              rows={3}
              data-testid="fit-blocked-domains"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-blocked-emails">Blocked emails</Label>
            <Textarea
              id="fit-blocked-emails"
              value={fit_gate.blocked_emails}
              onChange={(event) => setField('blocked_emails', event.target.value)}
              placeholder="noreply@example.com"
              rows={3}
              data-testid="fit-blocked-emails"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-blocked-companies">Blocked companies</Label>
            <Textarea
              id="fit-blocked-companies"
              value={fit_gate.blocked_companies}
              onChange={(event) => setField('blocked_companies', event.target.value)}
              placeholder="Commune de Test"
              rows={3}
              data-testid="fit-blocked-companies"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label htmlFor="fit-workspace-suppressions">Workspace suppressions</Label>
            <Textarea
              id="fit-workspace-suppressions"
              value={fit_gate.workspace_suppressions}
              onChange={(event) => setField('workspace_suppressions', event.target.value)}
              placeholder="email:noreply@example.com&#10;domain:optout-domain.com"
              rows={4}
              data-testid="fit-workspace-suppressions"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-brand-suppressions">Brand suppressions</Label>
            <Textarea
              id="fit-brand-suppressions"
              value={fit_gate.brand_suppressions}
              onChange={(event) => setField('brand_suppressions', event.target.value)}
              placeholder="company_name:Ville de Test"
              rows={4}
              data-testid="fit-brand-suppressions"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-campaign-suppressions">Campaign suppressions</Label>
            <Textarea
              id="fit-campaign-suppressions"
              value={fit_gate.campaign_suppressions}
              onChange={(event) => setField('campaign_suppressions', event.target.value)}
              placeholder="email:maire@example.fr"
              rows={4}
              data-testid="fit-campaign-suppressions"
            />
          </div>
        </div>
        <Hint>
          Format suppression: une entrée par ligne, sous la forme email:...,
          domain:..., company_domain:... ou company_name:...
        </Hint>

        <div className="grid grid-cols-2 gap-4" data-testid="fit-thresholds">
          <div className="space-y-1">
            <Label htmlFor="fit-review-threshold">Review threshold</Label>
            <Input
              id="fit-review-threshold"
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={fit_gate.review_threshold}
              onChange={(event) => setField('review_threshold', Number(event.target.value))}
              data-testid="fit-review-threshold"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fit-send-threshold">Send threshold</Label>
            <Input
              id="fit-send-threshold"
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={fit_gate.send_threshold}
              onChange={(event) => setField('send_threshold', Number(event.target.value))}
              data-testid="fit-send-threshold"
            />
          </div>
        </div>

        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Les leads en <strong>review</strong> ne partent pas chez le provider: ils alimentent
          une file ops à valider avant envoi.
        </div>
      </CardContent>
    </Card>
  );
}
